import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faSquarePlus, faSquareXmark, faTrash} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';

import {RegisterService} from '../../../services/register.service';
import {AuthService} from '../../../services/auth.service';

@Component({
    selector: 'app-register-form',
    imports: [CommonModule, ReactiveFormsModule, FontAwesomeModule],
    templateUrl: './register-form.component.html',
    styleUrl: './register-form.component.css'
})
export class RegisterFormComponent implements OnInit {
  registerForm: FormGroup;
  locations: any[] = [];
  registerId: number | null = null;
  isEditMode: boolean = false;
  isAdminUser: boolean = false;

  constructor(
    private fb: FormBuilder,
    private registerService: RegisterService,
    private auth: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    library: FaIconLibrary
  ) {
    library.addIcons(faSquarePlus, faTrash, faSquareXmark);

    this.registerForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      scope_type: ['ORG', Validators.required],
      location: [null],
      is_active: [true],
      workflow_enabled: [false],
      review_required: [false],
      approval_required: [false],
      otp_required: [false],
      qr_required: [false],
      allow_bulk_upload: [false],
      steps: this.fb.array([])
    });
  }

  ngOnInit(): void {
    const roles = this.getUserRoles();
      const isPlatformSuperuser = this.auth.isSuperUser();
      this.isAdminUser = roles.includes('Admin') && !isPlatformSuperuser;
      if (isPlatformSuperuser) {
      this.router.navigate(['/dashboard']);
      return;
    }
    if (!this.isAdminUser) {
      this.router.navigate(['/visitor-management/select-register']);
      return;
    }
    this.fetchLocations();
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.registerId = Number(params['id']);
        this.isEditMode = true;
        this.loadRegister(this.registerId);
      } else {
        this.addStep();
      }
    });

    this.registerForm.get('scope_type')?.valueChanges.subscribe(value => {
      const locationControl = this.registerForm.get('location');
      if (value === 'ORG') {
        this.registerForm.patchValue({ location: null });
        locationControl?.clearValidators();
      } else {
        locationControl?.setValidators([Validators.required]);
      }
      locationControl?.updateValueAndValidity();
    });
  }

  private getUserRoles(): string[] {
    const userInfo = this.auth.getCurrentUserInfo();
    const role = userInfo?.role;
    if (Array.isArray(role)) {
      return role;
    }
    return role ? [role] : [];
  }

  get stepsArray(): FormArray<FormGroup> {
  return this.registerForm.get('steps') as FormArray<FormGroup>;
    }

  addStep(label: string = ''): void {
    const stepNumber = this.stepsArray.length + 1;
    this.stepsArray.push(this.fb.group({
      step: [stepNumber, Validators.required],
      label: [label || `Step ${stepNumber}`, Validators.required]
    }));
  }

  removeStep(index: number): void {
    if (this.stepsArray.length <= 1) {
      return;
    }
    this.stepsArray.removeAt(index);
    this.reindexSteps();
  }

  reindexSteps(): void {
    this.stepsArray.controls.forEach((control, index) => {
      control.get('step')?.setValue(index + 1);
    });
  }

  fetchLocations(): void {
    this.registerService.listLocations(1, 200).subscribe({
      next: (res: any) => {
        this.locations = res?.results ?? res ?? [];
      }
    });
  }

  loadRegister(id: number): void {
    this.registerService.getRegister(id).subscribe({
      next: (register: any) => {
        this.registerForm.patchValue({
          name: register.name,
          description: register.description,
          scope_type: register.scope_type,
          location: register.location,
          is_active: register.is_active,
          workflow_enabled: register.workflow_enabled,
          review_required: register.review_required,
          approval_required: register.approval_required,
          otp_required: register.otp_required,
          qr_required: register.qr_required,
          allow_bulk_upload: register.allow_bulk_upload
        });
        const locationControl = this.registerForm.get('location');
        if (register.scope_type === 'LOCATION') {
          locationControl?.setValidators([Validators.required]);
        } else {
          locationControl?.clearValidators();
        }
        locationControl?.updateValueAndValidity();
        this.stepsArray.clear();
        if (Array.isArray(register.steps_config) && register.steps_config.length) {
          register.steps_config.forEach((step: any) => {
            this.stepsArray.push(this.fb.group({
              step: [step.step, Validators.required],
              label: [step.label, Validators.required]
            }));
          });
        } else {
          this.addStep();
        }
      }
    });
  }

  buildStepsConfig(): any[] {
    return this.stepsArray.controls.map(control => ({
      step: control.get('step')?.value,
      label: control.get('label')?.value
    }));
  }

  submit(): void {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }
    const formValue = this.registerForm.value;
    const payload = {
      name: formValue.name,
      description: formValue.description,
      scope_type: formValue.scope_type,
      location: formValue.scope_type === 'ORG' ? null : formValue.location,
      is_active: formValue.is_active,
      workflow_enabled: formValue.workflow_enabled,
      review_required: formValue.review_required,
      approval_required: formValue.approval_required,
      otp_required: formValue.otp_required,
      qr_required: formValue.qr_required,
      allow_bulk_upload: formValue.allow_bulk_upload,
      steps_config: this.buildStepsConfig()
    };
    if (this.isEditMode && this.registerId) {
      this.registerService.updateRegister(this.registerId, payload).subscribe({
        next: () => {
          this.toastr.success('Register updated successfully.', 'Success');
          this.router.navigate(['/visitor-management/registers']);
        }
      });
    } else {
      this.registerService.createRegister(payload).subscribe({
        next: () => {
          this.toastr.success('Register created successfully.', 'Success');
          this.router.navigate(['/visitor-management/registers']);
        }
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/visitor-management/registers']);
  }

  goToFields(): void {
    if (this.registerId) {
      this.router.navigate([`/visitor-management/registers/${this.registerId}/fields`]);
    }
  }
}
